import type { MutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useIsProjectControlButtonDisabled } from 'enteties/project/hooks/useIsProjectControlButtonDisabled';
import { Pause, Play } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { Button } from 'shared/ui/button';
import { TooltipWrapper } from 'shared/ui/tooltips';
import type { Project } from '@/enteties/project/modal';
import type { StatusResponse } from 'shared/api/services/types';
import { useProjectQuerySlice } from 'features/start-stop-project/hooks/useProjectQuerySlice';
import * as m from '@paraglide/messages';
import { useIsStartStopDisabled } from '@/enteties/project/hooks/useIsStartStopDisabled';
export interface MutationOptionsProps<TData extends StatusResponse> {
  mutationOptions: MutationOptions<TData, Error, number>;
  slug: string;
  project: Project;
}
interface StartStopProjectProps<TData extends StatusResponse> extends MutationOptionsProps<TData> {
  isTimedOut: boolean;
  stopBuildMutationOptions?: MutationOptions<Project, Error, void>;
}
export const StartStopProject = <TData extends StatusResponse,>({
  mutationOptions,
  slug,
  project,
  isTimedOut,
  stopBuildMutationOptions
}: StartStopProjectProps<TData>) => {
  const {
    setProjectProperty,
    setProjectArrayProperty
  } = useProjectQuerySlice(slug);
  const {
    mutate: controlProject
  } = useMutation({
    ...mutationOptions,
    onSuccess: (data, instance) => {
      if (data.status === 'OK') {
        setProjectProperty({
          requiredInstances: instance
        });
        setProjectArrayProperty(proj => proj.slug === project.slug, {
          requiredInstances: instance
        });
      }
    }
  });
  const {
    mutate: stopBuild
  } = useMutation({
    ...stopBuildMutationOptions,
    onSuccess: data => {
      setProjectProperty({
        status: 'BUILD_STOPPED'
      });
      setProjectArrayProperty(proj => proj.slug === project.slug, {
        status: 'BUILD_STOPPED'
      });
    }
  });
  const isPaused = useMemo(() => {
    if (!project) return false;
    return project.requiredInstances > 0 ? project.status === 'STOPPED' || project.status === 'EMPTY' && project.serviceType === 'marketplace' : project.status !== 'BUILD_STARTED';
  }, [project]);
  const handleStart = useCallback(() => {
    controlProject(1);
  }, [controlProject]);
  const handlePause = useCallback(() => {
    if (project.serviceType === 'compute' && project.status === 'BUILD_STARTED' && stopBuildMutationOptions) {
      stopBuild();
    } else {
      controlProject(0);
    }
  }, [controlProject, project.serviceType, project.status, stopBuild, stopBuildMutationOptions]);
  const isStartStopDisabled = useIsStartStopDisabled(project);
  const isDefaultDisabled = useIsProjectControlButtonDisabled(project, slug);
  const isDisabled = useMemo(() => isTimedOut && project.status !== 'BUILD_STARTED' || isDefaultDisabled || isStartStopDisabled, [isDefaultDisabled, isStartStopDisabled, isTimedOut, project.status]);
  return <TooltipWrapper content={`${isPaused ? m.project_ctrl_btns_start() : m.project_ctrl_btns_stop()} ${m.project_ctrl_btns_project()}`} data-sentry-element="TooltipWrapper" data-sentry-component="StartStopProject" data-sentry-source-file="start-stop-project.tsx">
      <div>
        {isPaused ? <Button onClick={handleStart} size="icon-highlight" variant="highlighted" disabled={isDisabled}>
            <Play className="size-5" />
          </Button> : <Button onClick={handlePause} size="icon-highlight" variant="highlighted" disabled={isDisabled}>
            <Pause className="size-5" />
          </Button>}
      </div>
    </TooltipWrapper>;
};